import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
	Alert,
	Autocomplete,
	Box,
	Button,
	Checkbox,
	Chip,
	CircularProgress,
	Divider,
	Drawer,
	Grid,
	IconButton,
	InputAdornment,
	Paper,
	Stack,
	Tab,
	Tabs,
	TextField,
	Tooltip,
	Typography,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import SearchIcon from "@mui/icons-material/Search";
import TimelineIcon from "@mui/icons-material/Timeline";
import CloseIcon from "@mui/icons-material/Close";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PauseIcon from "@mui/icons-material/Pause";
import CancelIcon from "@mui/icons-material/Cancel";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import FileDownloadIcon from "@mui/icons-material/FileDownloadOutlined";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import Swal from "sweetalert2";
// xlsx-js-style, not xlsx: identical SheetJS API plus the `s` cell-style property.
// The plain community build of `xlsx` silently DROPS fills and fonts, so the report
// would have written fine and come out entirely unstyled.
import * as XLSX from "xlsx-js-style";
import Datagrid from "./Datagrid/Datagrid";
import HeaderTitle from "./HeaderTitle/HeaderTitle";
import {
	clearDocumentSyncRequests,
	clearStepSyncRequests,
	getDocumentSyncRequests,
	getPipelineMonitorSummary,
	resetStepSyncRequest,
	holdStepSyncRequest,
	cancelStepSyncRequest,
	deleteStepSyncRequest,
	getStepSyncRequests,
	getStepSyncRequestEvents,
	exportStepSyncRequests,
} from "../services/adminService";
import { askRefreshMode } from "../utilities/refreshMode";
import { getSocket } from "../services/socket";
import { getAllCustomerSites } from "../services/manageSitesMasterService";
import { getMachinesByProvision } from "../services/analyticsService";

// Only statuses something actually writes to step_sync_requests — offering a filter
// value that can never match is worse than no filter, because an empty grid reads as
// "nothing happened" rather than "that state does not exist". Dropped from this list:
// 'completed' (a DOCUMENT sync status; STEP writes 'complete'), 'superseded' and
// 'uploaded' (both only ever appear in constant lists, never written). Added
// 'conversion_failed', which IS written by the convert webhook but was missing here.
// Written by: the pipeline's _update_request_status (pending, processing,
// ready_to_submit, awaiting_sharepoint, expanded, up_to_date, not_found,
// not_found_in_bluestar, heavy_model, upload_failed, failed_to_download,
// submitted_to_model_convertor), the convert webhook (conversion_completed,
// finalizing_model, complete, conversion_failed) and the portal's own hold/cancel
// actions (on_hold, cancelled).
const STEP_STATUS_OPTIONS = [
	"pending",
	"processing",
	"ready_to_submit",
	"awaiting_sharepoint",
	"expanded",
	"submitted_to_model_convertor",
	"conversion_completed",
	"finalizing_model",
	"complete",
	"up_to_date",
	"on_hold",
	"cancelled",
	"not_found",
	"not_found_in_bluestar",
	"heavy_model",
	"failed_to_download",
	"upload_failed",
	"conversion_failed",
];

const DOCUMENT_STATUS_OPTIONS = [
	"pending",
	"processing",
	"complete",
	"completed",
	"failed",
];

const buildRequestFilters = (filterModel) => {
	const items = Array.isArray(filterModel?.items) ? filterModel.items : [];
	const result = {};

	for (const item of items) {
		const field = String(item?.field || "").trim();
		const value = String(item?.value ?? "").trim();
		if (!field || !value) continue;

		if (field === "id") result.id = value;
		if (field === "machine_number") result.machine_number = value;
		if (field === "status") result.status = value;
		if (field === "source_display") result.source = value;
		if (field === "updated_at") result.updated = value;
		if (field === "last_error") result.last_error = value;
	}

	return result;
};

const formatDateTime = (value) => {
	if (!value) return "—";
	const date = new Date(value);
	if (Number.isNaN(date.getTime())) return "—";
	return date.toLocaleString();
};

const statusColor = (status = "") => {
	switch (String(status).toLowerCase()) {
		// success (green). 'up_to_date' belongs here: terminal AND viewable -- the
		// source was unchanged and the published GLB is still there. It used to sit
		// in the blue "in progress" bucket, which told customers work was ongoing on
		// machines that were finished, and disagreed with the server, where
		// up_to_date counts as complete.
		case "complete":
		case "completed":
		case "up_to_date":
		case "conversion_completed":
		case "found_in_bluestar":
		case "found_in_sharepoint":
		case "ok":
			return "success";
		// info (blue)
		case "pending":
		case "processing":
		case "ready_to_submit":
		case "submitted_to_model_convertor":
		case "finalizing_model":
		case "started":
		case "uploading":
		case "downloading":
		case "polling":
		case "submitted":
			return "info";
		// A parent row that fanned out into numbered split children: finished itself,
		// but NOT a delivered model — the real outcome is on the children, so it is
		// shown as "Parent" rather than as a success or a failure. It used to fall
		// through to the grey `default`, which read as an unknown state.
		case "expanded":
			return "secondary";
		// warning (amber)
		case "awaiting_sharepoint":
		case "rate_check":
		case "blocked":
		case "on_hold":
		case "sharepoint_check_failed":
		case "stuck_requeued":
		case "requeued":
		case "superseded":
		case "skipped":
		case "republish_missing_glb":
			return "warning";
		// error (red)
		case "not_found":
		case "not_found_in_bluestar":
		case "not_found_in_sharepoint":
		case "upload_failed":
		case "failed_to_download":
		case "heavy_model":
		case "conversion_failed":
		case "failed":
		case "cancelled":
		case "error":
			return "error";
		default:
			return "default";
	}
};

// Plain-English status labels. The grid used to render the raw column value, which
// put internal words ('submitted_to_model_convertor', 'expanded', 'heavy_model') in
// front of people who are not developers. The raw value is kept in the chip tooltip
// so a support conversation can still name the exact status.
const STATUS_LABEL = {
	pending: "Queued",
	processing: "Searching for CAD",
	ready_to_submit: "Waiting for a slot",
	awaiting_sharepoint: "Checking SharePoint",
	expanded: "Parent",
	submitted_to_model_convertor: "Converting",
	conversion_completed: "Converted",
	finalizing_model: "Publishing",
	complete: "Model ready",
	completed: "Model ready",
	up_to_date: "Already up to date",
	superseded: "Replaced by newer request",
	on_hold: "On hold",
	cancelled: "Cancelled",
	not_found: "No CAD file found",
	not_found_in_bluestar: "No CAD file found",
	heavy_model: "Too large to convert",
	failed_to_download: "Download failed",
	upload_failed: "Upload failed",
	conversion_failed: "Conversion failed",
};

const statusLabel = (status) => {
	const key = String(status || "").trim().toLowerCase();
	if (!key) return "—";
	return STATUS_LABEL[key] || status;
};

const documentSyncResult = (row) => {
	let payload = row?.payload;
	if (typeof payload === "string") {
		try {
			payload = JSON.parse(payload);
		} catch {
			return null;
		}
	}
	return payload?.document_sync_result || null;
};

const documentStatusLabel = (status, row) => {
	const key = String(status || "").trim().toLowerCase();
	if (key === "completed" || key === "complete") {
		const result = documentSyncResult(row);
		const found = Number(result?.files_found ?? result?.documents_found);
		return Number.isFinite(found) ? (found === 0 ? "Completed — no documents found" : "Completed") : "Completed";
	}
	return ({
		pending: "Queued",
		processing: "Syncing documents",
		failed: "Failed",
	})[key] || status || "—";
};

const documentResultLabel = (row) => {
	const result = documentSyncResult(row);
	if (!result) return "Not recorded";
	const found = Number(result.files_found ?? result.documents_found);
	if (!Number.isFinite(found)) return "Not recorded";
	if (found === 0) return "No documents found";
	const uploaded = Number(result.files_uploaded ?? result.documents_uploaded);
	return Number.isFinite(uploaded) ? `Found ${found}; uploaded ${uploaded}` : `Found ${found}`;
};

// Where a row sits in a multi-part machine. Split children are named
// '<machine>_01', '_02', … by the pipeline, so the suffix IS the part number; a
// parent is the row that fanned out (status 'expanded'). Single-file machines have
// no hierarchy and get no chip — most rows are that case and don't need the noise.
const partRole = (row) => {
	const item = String(row?.item_number || "").trim();
	const match = item.match(/_(\d+)$/);
	if (match) return `Child ${Number(match[1])}`;
	if (String(row?.status || "").trim().toLowerCase() === "expanded") return "Parent";
	return "";
};

// Terminal statuses — the request is finished, for better or worse. Anything else
// that is not 'pending' is work actually in flight.
const TERMINAL_STATUSES = [
	"complete",
	"completed",
	"up_to_date",
	"superseded",
	"expanded",
	"cancelled",
	"not_found",
	"not_found_in_bluestar",
	"heavy_model",
	"failed_to_download",
	"upload_failed",
	"conversion_failed",
];

/**
 * What the timeline header should say about this request right now.
 *
 * The chip used to report the SOCKET state ("Live"/"Offline"), which said nothing
 * about the request itself — a finished request from last week still showed "Live"
 * purely because the browser had a websocket. Live now means work is actually
 * running; a queued request reads "Not started" and a finished one "Completed".
 *
 * @param {object} row The request row (needs `status` and `started_at`).
 * @param {boolean} socketConnected Whether the event socket is currently connected.
 * @returns {{label: string, color: string}} Chip label + MUI colour.
 */
const runStateChip = (row, socketConnected) => {
	const status = String(row?.status || "").trim().toLowerCase();
	if (!status) return { label: "Not started", color: "default" };
	if (TERMINAL_STATUSES.includes(status)) return { label: "Completed", color: statusColor(status) };
	// started_at is stamped when the pipeline first claims the row, so a 'pending'
	// row that has never been claimed is genuinely not started yet.
	if (status === "pending" && !row?.started_at) return { label: "Not started", color: "default" };
	return socketConnected
		? { label: "Live", color: "success" }
		: { label: "Running (not streaming)", color: "warning" };
};

// The customer report speaks four words, and only four. A bakery is asking one
// question -- can I open this model? -- so every pipeline status collapses into
// the answer to that, plus the reason when the answer is no.
//
// Keyed on the status itself, NOT on the chip colour. The colour buckets are a UI
// signal and are deliberately coarser: 'awaiting_sharepoint' and 'superseded'
// share the amber bucket but mean opposite things to a customer. Anything not
// mapped falls back to Not Available, which is the safe answer -- never claim a
// model is there.
const RESULT_MODEL_AVAILABLE = "Model Available";
const RESULT_NOT_AVAILABLE = "Not Available";
const RESULT_NOT_FOUND = "Not Found";
const RESULT_FAILED_COMPLEX = "Failed Due to Complexity";

const RESULT_BY_STATUS = {
	complete: RESULT_MODEL_AVAILABLE,
	completed: RESULT_MODEL_AVAILABLE,
	up_to_date: RESULT_MODEL_AVAILABLE,

	// No CAD file exists on our side. Nothing the customer can do, and it will not
	// change until someone at AMF supplies one -- kept separate from a failure so
	// the two are not read as the same problem.
	not_found: RESULT_NOT_FOUND,
	not_found_in_bluestar: RESULT_NOT_FOUND,
	not_found_in_sharepoint: RESULT_NOT_FOUND,

	// The CAD exists but defeated the converter -- too large, or it errored out.
	heavy_model: RESULT_FAILED_COMPLEX,
	conversion_failed: RESULT_FAILED_COMPLEX,
};

// Plain reason, shown only when the answer is not "Model Available". Deliberately
// says nothing about BlueStar, SharePoint, queues or converters: the stored
// last_error names our own systems, which tells a bakery nothing and leaks our
// plumbing into their report.
const RESULT_DETAIL = {
	not_found: "No CAD file on record for this machine.",
	not_found_in_bluestar: "No CAD file on record for this machine.",
	not_found_in_sharepoint: "No CAD file on record for this machine.",
	heavy_model: "The CAD file is too large to turn into a 3D model.",
	conversion_failed: "The CAD file could not be turned into a 3D model.",
	failed_to_download: "The CAD file could not be retrieved. AMF has been notified.",
	upload_failed: "The model was built but could not be published. AMF has been notified.",
	superseded: "Replaced by a newer request for the same machine.",
	expanded: "This machine is split into parts - see the rows below.",
	on_hold: "On hold.",
	cancelled: "Cancelled.",
};

/**
 * The outcome word for one row, and the reason when it is not available.
 *
 * @param {object} row A request row from the grid.
 * @returns {{result: string, detail: string}} detail is "" for available models
 *   and for in-flight rows, where "being prepared" is the whole story.
 */
const customerOutcome = (row) => {
	const key = String(row?.status || "").trim().toLowerCase();
	const result = RESULT_BY_STATUS[key] || RESULT_NOT_AVAILABLE;
	if (result === RESULT_MODEL_AVAILABLE) return { result, detail: "" };
	const detail = RESULT_DETAIL[key] || (statusColor(key) === "info" ? "Being prepared." : "");
	return { result, detail };
};

// Row fills keyed by the same outcome the Result column carries, so a reader scans
// colour first and only reads the words when something is off. ARGB, Excel's order.
const RESULT_STYLE = {
	[RESULT_MODEL_AVAILABLE]: { fill: "FFE8F5E9", text: "FF1B5E20" },
	[RESULT_NOT_AVAILABLE]: { fill: "FFF5F5F5", text: "FF424242" },
	[RESULT_NOT_FOUND]: { fill: "FFFFF8E1", text: "FF8D6E00" },
	[RESULT_FAILED_COMPLEX]: { fill: "FFFDECEA", text: "FFB71C1C" },
};

const CELL_BORDER = {
	top: { style: "thin", color: { rgb: "FFE0E0E0" } },
	bottom: { style: "thin", color: { rgb: "FFE0E0E0" } },
	left: { style: "thin", color: { rgb: "FFE0E0E0" } },
	right: { style: "thin", color: { rgb: "FFE0E0E0" } },
};

/**
 * Download the selected requests as a styled .xlsx report.
 *
 * Written for the customer, not for us. Four outcomes only -- Model Available,
 * Not Available, Not Found, Failed Due to Complexity -- with a plain sentence of
 * detail when the answer is not "available".
 *
 * The pipeline's own vocabulary is deliberately absent: no raw status column
 * ('submitted_to_model_convertor', 'expanded', 'up_to_date' mean nothing to a
 * bakery and only invite questions), and no last_error, which names BlueStar and
 * SharePoint -- our systems, not theirs. Support can still read the exact status
 * from the on-screen grid, where the chip tooltip keeps the raw value.
 *
 * Rows are tinted by outcome: green available / grey not available / amber not
 * found / red failed.
 *
 * @param {Array<object>} rows Selected request rows, straight from the grid.
 * @returns {void} Triggers the browser download; no-ops on an empty selection.
 */
const exportStepRowsToXLSX = (rows) => {
	const list = Array.isArray(rows) ? rows : [];
	if (!list.length) return;

	const header = [
		"Bakery Location Number",
		"Customer Name",
		"Machine Number",
		"Part",
		"Result",
		"Details",
		"Date",
		"Requested By",
	];

	const body = list.map((row) => {
		const item = String(row?.item_number || "").trim();
		const machine = String(row?.machine_number || "").trim();
		const displayMachine = item && item !== machine ? item : machine;
		const when = row?.completed_at || row?.updated_at || row?.requested_at || null;
		const outcome = customerOutcome(row);
		return [
			row?.customer_site_number || "",
			row?.customer_site_name || "",
			displayMachine || "",
			partRole(row) || "Single model",
			outcome.result,
			outcome.detail,
			when ? formatDateTime(when) : "",
			row?.requested_by || "Automated pipeline",
		];
	});

	const worksheet = XLSX.utils.aoa_to_sheet([header, ...body]);
	worksheet["!cols"] = [
		{ wch: 20 }, { wch: 28 }, { wch: 20 }, { wch: 14 },
		{ wch: 24 }, { wch: 56 }, { wch: 22 }, { wch: 24 },
	];
	// Header stays sortable/filterable in Excel without the reader setting it up.
	// (No frozen header: `!freeze` is a SheetJS Pro feature — this build writes the
	// sheetView with no <pane> element, so setting it would be silently dead code.)
	worksheet["!autofilter"] = { ref: `A1:${String.fromCharCode(64 + header.length)}${body.length + 1}` };

	header.forEach((_, col) => {
		const cell = worksheet[XLSX.utils.encode_cell({ r: 0, c: col })];
		if (!cell) return;
		cell.s = {
			font: { bold: true, sz: 11, color: { rgb: "FFFFFFFF" } },
			fill: { patternType: "solid", fgColor: { rgb: "FF1F3864" } },
			alignment: { horizontal: "left", vertical: "center" },
			border: CELL_BORDER,
		};
	});

	// Column index 4 is Result — the value the row's tint is keyed on.
	body.forEach((dataRow, index) => {
		const style = RESULT_STYLE[dataRow[4]] || RESULT_STYLE[RESULT_NOT_AVAILABLE];
		header.forEach((_, col) => {
			const cell = worksheet[XLSX.utils.encode_cell({ r: index + 1, c: col })];
			if (!cell) return;
			cell.s = {
				font: { sz: 10, color: { rgb: style.text }, bold: col === 4 },
				fill: { patternType: "solid", fgColor: { rgb: style.fill } },
				// Details are full sentences; wrapping keeps the column readable
				// instead of spilling across every cell to its right.
				alignment: { vertical: "top", wrapText: col === 5 },
				border: CELL_BORDER,
			};
		});
	});

	const workbook = XLSX.utils.book_new();
	XLSX.utils.book_append_sheet(workbook, worksheet, "STEP Sync");
	const stamp = new Date().toISOString().slice(0, 10);
	XLSX.writeFile(workbook, `step_sync_report_${stamp}.xlsx`);
};

// Ordered stages of a request's lifecycle for the live timeline.
const STAGE_LABEL = {
	search: "Searching sources",
	expanded: "Expanded",
	freshness: "Freshness check",
	staged: "Staged (file found)",
	rate_check: "Quota check",
	submitted: "Submitted to model-convert",
	convert: "Model convert",
	download: "Download",
	conversion_completed: "Conversion completed",
	conversion_failed: "Conversion failed",
	finalized: "Published + cached",
	superseded: "Superseded (newer request)",
};

const normalizeValue = (value) => String(value ?? "").toLowerCase();

/**
 * Turn an API list into de-duplicated, sorted {value,label} filter options.
 *
 * The value is the number the server filters on (site number / machine number); the
 * name is only ever decoration on the label, so a row with no name still yields a
 * usable option instead of being dropped.
 *
 * @param {Array<object>} rows Raw API rows.
 * @param {string} valueKey Field holding the number to filter by.
 * @param {string} nameKey Field holding the human name.
 * @returns {Array<{value: string, label: string}>} Sorted, unique options.
 */
const toFilterOptions = (rows, valueKey, nameKey) => {
	const seen = new Set();
	const options = [];
	for (const row of Array.isArray(rows) ? rows : []) {
		const value = String(row?.[valueKey] || "").trim();
		if (!value || seen.has(value)) continue;
		seen.add(value);
		const name = String(row?.[nameKey] || "").trim();
		// "EMPTY" is a real placeholder in the sites data, not a name.
		options.push({ value, label: name && name !== "EMPTY" ? `${value} — ${name}` : value });
	}
	return options.sort((a, b) => a.label.localeCompare(b.label));
};

// Options for the status picker, in the same plain-English wording as the chips.
const STATUS_FILTER_OPTIONS = STEP_STATUS_OPTIONS.map((value) => ({ value, label: statusLabel(value) }));

// Most options rendered at once. The machine list is thousands of rows and MUI's
// Autocomplete renders every match, so an unfiltered open would jank the page. The
// user types to narrow; the cap only ever hides matches beyond the hundredth.
const MAX_VISIBLE_OPTIONS = 100;

/**
 * Multi-select filter dropdown, in the same MUI Autocomplete style the Documentation
 * screens use — with `multiple` + checkboxes, which none of those instances have.
 *
 * Options are {value,label} objects but the selection is exposed to the caller as a
 * plain array of values, because that is what goes on the wire (comma-joined) and it
 * keeps the URL/query readable.
 *
 * @param {object} props Component props.
 * @param {string} props.label Field label.
 * @param {Array<{value: string, label: string}>} props.options Available options.
 * @param {string[]} props.value Currently selected values.
 * @param {Function} props.onChange Called with the next array of values.
 * @param {boolean} [props.loading] Show a spinner while options load.
 * @param {boolean} [props.disabled] Disable the control.
 * @param {number} [props.width] Min width in px.
 * @returns {JSX.Element} The dropdown.
 */
const MultiFilterSelect = ({ label, options = [], value = [], onChange, onOpen, loading, disabled, width = 240 }) => {
	const selectedValues = Array.isArray(value) ? value : [];
	// Keep a selection visible even if its option hasn't loaded yet, so a restored or
	// in-flight filter never silently drops off the screen while still being applied.
	const selectedOptions = selectedValues.map(
		(selected) => options.find((option) => option.value === selected) || { value: selected, label: selected }
	);

	return (
		<Autocomplete
			multiple
			disableCloseOnSelect
			size="small"
			limitTags={1}
			disabled={disabled}
			loading={loading}
			options={options}
			value={selectedOptions}
			onChange={(event, picked) => onChange?.(picked.map((option) => option.value))}
			onOpen={onOpen}
			isOptionEqualToValue={(option, selected) => option.value === selected.value}
			getOptionLabel={(option) => option?.label || ""}
			filterOptions={(available, state) => {
				const query = state.inputValue.trim().toLowerCase();
				const matched = query
					? available.filter((option) => String(option.label).toLowerCase().includes(query))
					: available;
				return matched.slice(0, MAX_VISIBLE_OPTIONS);
			}}
			renderOption={(props, option, { selected }) => (
				<li {...props} key={option.value}>
					<Checkbox size="small" checked={selected} sx={{ mr: 1, p: 0.5 }} />
					{option.label}
				</li>
			)}
			renderTags={(tags, getTagProps) =>
				tags.map((option, index) => (
					<Chip size="small" label={option.label} {...getTagProps({ index })} key={option.value} />
				))
			}
			sx={{ minWidth: width, maxWidth: width * 1.6 }}
			renderInput={(params) => <TextField {...params} label={label} placeholder={selectedValues.length ? "" : label} />}
		/>
	);
};

const sanitizeRequestRows = (rows) => {
	if (!Array.isArray(rows)) return [];

	return rows.filter((row) => {
		if (!row || typeof row !== "object") return false;
		return row.id !== null && row.id !== undefined && row.id !== "";
	});
};

/**
 * Summary card for one pipeline queue: headline total plus a chip per bucket.
 *
 * @param {string} title - card heading, e.g. "STEP Sync".
 * @param {Object} totals - bucketed counts from the server
 *   ({total, active, failed, complete, skipped, other, last_requested_at,
 *   last_completed_at}). The buckets are exhaustive server-side, so
 *   active + failed + complete + skipped + other === total.
 * @param {Object} counts - raw per-status counts; only used as a fallback for
 *   `complete` so an older server build that predates `totals.complete` still
 *   renders a real number instead of 0.
 * @param {Function} t - i18next translate.
 *
 * Three chips by design. There was a fourth, "Skipped", for rows that closed
 * without a fresh conversion; the server now folds those into Complete
 * ('expanded', 'up_to_date', 'superseded' — a usable model is present) and
 * Failed ('heavy_model' — over the size ceiling, no model, actionable). The
 * chip is kept behind `skipped > 0` only so an older server build that still
 * returns the bucket does not silently break the sum; against a current server
 * it never renders.
 * "Other" only renders when non-zero; it means the pipeline wrote a status this
 * build does not know about, and is a prompt to update
 * iEcom_server/src/constants/stepSyncStatus.js.
 */
const StatCard = ({ title, totals = {}, counts = {}, t }) => {
	const complete = totals.complete ?? ((counts.complete || 0) + (counts.completed || 0));
	const skipped = totals.skipped || 0;
	const other = totals.other || 0;

	return (
	<Paper variant="outlined" sx={{ p: 2, height: "100%", borderRadius: 2 }}>
		<Typography variant="subtitle2" color="text.secondary" gutterBottom>
			{title}
		</Typography>
		<Typography variant="h5" fontWeight={700}>
			{totals.total || 0}
		</Typography>
		<Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: "wrap", rowGap: 1 }}>
			<Chip label={`${t("UI_TEXT.ACTIVE")}: ${totals.active || 0}`} color="info" size="small" />
			<Chip label={`${t("UI_TEXT.FAILED")}: ${totals.failed || 0}`} color="error" size="small" />
			<Chip label={`${t("UI_TEXT.COMPLETE")}: ${complete}`} color="success" size="small" />
			{skipped > 0 && (
				<Chip label={`${t("UI_TEXT.SKIPPED", { defaultValue: "Skipped" })}: ${skipped}`} color="default" size="small" />
			)}
			{other > 0 && (
				<Chip label={`${t("UI_TEXT.OTHER", { defaultValue: "Other" })}: ${other}`} color="warning" size="small" />
			)}
		</Stack>
		<Typography variant="caption" display="block" sx={{ mt: 1.5 }}>
			{t("UI_TEXT.LAST_REQUESTED")}: {formatDateTime(totals.last_requested_at)}
		</Typography>
		<Typography variant="caption" display="block">
			{t("UI_TEXT.LAST_COMPLETED")}: {formatDateTime(totals.last_completed_at)}
		</Typography>
	</Paper>
	);
};

const PipelineTable = ({
	title,
	rows,
	loading,
	tableExists,
	type,
	t,
	onClearLogs,
	clearLogsLoading,
	rowCount,
	paginationModel,
	onPaginationModelChange,
	filterModel,
	onFilterModelChange,
	searchValue,
	onSearchChange,
	onViewTimeline,
	onStartStep,
	onHoldStep,
	onCancelStep,
	onDeleteStep,
	busy,
	selectedIds = [],
	onSelectionChange,
	onBulkStart,
	onBulkPause,
	onBulkStop,
	onBulkClear,
	bulkBusy,
	quickFilters = {},
	onQuickFilterChange,
	onQuickFilterReset,
	onSelectAllMatching,
	onClearSelection,
	onExportSelected,
	selectAllBusy,
	siteOptions = [],
	machineOptions = [],
	optionsLoading = false,
	machineOptionsLoading = false,
	onLoadMachineOptions,
}) => {
	const selectedCount = Array.isArray(selectedIds) ? selectedIds.length : 0;
	const bulkButtons = useMemo(() => {
		if (type !== "step") return undefined;
		const none = selectedCount === 0 || Boolean(bulkBusy);
		// The grid's own header checkbox only reaches the current page, so this covers
		// "everything matching the filters" — which is what an export of a day's work
		// actually needs. Flips to a clear once the whole matching set is selected.
		const totalMatching = Number(rowCount) || 0;
		const allMatchingSelected = totalMatching > 0 && selectedCount >= totalMatching;
		return [
			{
				label: allMatchingSelected
					? `${t("UI_TEXT.CLEAR_SELECTION", { defaultValue: "Clear selection" })}`
					: `${t("UI_TEXT.SELECT_ALL", { defaultValue: "Select all" })} (${totalMatching})`,
				color: "info",
				icon: selectAllBusy ? <CircularProgress size={16} /> : <DoneAllIcon />,
				onClick: allMatchingSelected ? onClearSelection : onSelectAllMatching,
				disabled: totalMatching === 0 || Boolean(selectAllBusy),
			},
			{ label: `${t("UI_TEXT.START_RERUN", { defaultValue: "Start" })} (${selectedCount})`, color: "success", icon: <PlayArrowIcon />, onClick: onBulkStart, disabled: none },
			{ label: `${t("UI_TEXT.HOLD", { defaultValue: "Pause" })} (${selectedCount})`, color: "warning", icon: <PauseIcon />, onClick: onBulkPause, disabled: none },
			{ label: `${t("UI_TEXT.CANCEL_KILL", { defaultValue: "Stop" })} (${selectedCount})`, color: "error", icon: <CancelIcon />, onClick: onBulkStop, disabled: none },
			{ label: `${t("UI_TEXT.REMOVE", { defaultValue: "Clear" })} (${selectedCount})`, color: "error", icon: <DeleteIcon />, onClick: onBulkClear, disabled: none },
			{ label: `${t("UI_TEXT.EXPORT", { defaultValue: "Export" })} (${selectedCount})`, color: "primary", icon: <FileDownloadIcon />, onClick: onExportSelected, disabled: selectedCount === 0 },
		];
	}, [type, selectedCount, rowCount, selectAllBusy, bulkBusy, onSelectAllMatching, onClearSelection, onExportSelected, onBulkStart, onBulkPause, onBulkStop, onBulkClear, t]);

	const columns = useMemo(() => {
		// {value, label} so the filter dropdown offers the same plain-English wording as
		// the chips; the value sent to the server stays the raw status.
		const statusOptions = (type === "step" ? STEP_STATUS_OPTIONS : DOCUMENT_STATUS_OPTIONS).map(
			(value) => ({ value, label: type === "step" ? statusLabel(value) : documentStatusLabel(value) })
		);
		const baseColumns = [
			{
				field: "id",
				headerName: t("TABLE_HEADERS.ID"),
				flex: 0.7,
				minWidth: 90,
				type: "number",
			},
			// STEP only — the document sync endpoint resolves no site.
			...(type === "step"
				? [{
					field: "customer_site_number",
					headerName: t("UI_TEXT.SITE", { defaultValue: "Site" }),
					flex: 1,
					minWidth: 150,
					type: "string",
					renderCell: (params) => (
						<Box sx={{ lineHeight: 1.3, py: 0.5 }}>
							<Typography variant="body2">{params?.value || "—"}</Typography>
							{params?.row?.customer_site_name && (
								<Typography variant="caption" color="text.secondary">
									{params.row.customer_site_name}
								</Typography>
							)}
						</Box>
					),
				}]
				: []),
			{
				field: "machine_number",
				headerName: t("UI_TEXT.MACHINE"),
				flex: 1,
				minWidth: 160,
				hideable: false,
				type: "string",
				// Split children share their parent's machine_number, so a machine with
				// N parts renders as N identical rows. Show item_number instead when it
				// differs (23274-1003_01, _02, …); machine-level rows are unchanged.
				// Called with (value, row) by MUI v7 and with a single ({row, value})
				// params object by Datagrid.js:241 — handle both.
				valueGetter: (value, row) => {
					const record = row || value?.row || {};
					const item = record.item_number;
					return item && item !== record.machine_number ? item : record.machine_number || "";
				},
				// The part number alone ('..._02') doesn't say what it belongs to, so the
				// role rides along with it. Stacked as a caption under the number — the
				// same shape as the Site and Requested By columns — because an inline
				// chip wrapped onto its own line at narrow widths and left the column
				// looking ragged, with some rows one line and some two.
				renderCell: (params) => {
					const role = type === "step" ? partRole(params?.row) : "";
					return (
						<Box sx={{ lineHeight: 1.3, py: 0.5, minWidth: 0 }}>
							<Typography variant="body2" noWrap>
								{params?.value || "—"}
							</Typography>
							{role && (
								<Typography
									variant="caption"
									noWrap
									sx={{ color: role === "Parent" ? "secondary.main" : "text.secondary", fontWeight: role === "Parent" ? 600 : 400 }}
								>
									{role}
								</Typography>
							)}
						</Box>
					);
				},
			},
			{
				field: "status",
				headerName: t("UI_TEXT.STATUS"),
				flex: 1,
				minWidth: 180,
				type: "singleSelect",
				valueOptions: statusOptions,
				renderCell: (params) => (
					<Tooltip title={params.value || ""} placement="top" arrow>
						<Chip
							size="small"
							label={type === "step" ? statusLabel(params.value) : documentStatusLabel(params.value, params.row)}
							color={statusColor(params.value)}
						/>
					</Tooltip>
				),
			},
			...(type === "doc"
				? [{
					field: "document_result",
					headerName: t("UI_TEXT.DOCUMENT_RESULT", { defaultValue: "Document result" }),
					flex: 1.5,
					minWidth: 220,
					sortable: false,
					filterable: false,
					renderCell: (params) => (
						<Typography variant="body2" sx={{ whiteSpace: "normal", py: 0.5 }}>
							{documentResultLabel(params.row)}
						</Typography>
					),
				}]
				: []),
			{
				field: "source_display",
				headerName: t("UI_TEXT.SOURCE"),
				flex: 1.1,
				minWidth: 180,
				type: "string",
				valueGetter: (value, row) => {
					return row?.requested_source || row?.request_type || "—";
				},
			},
			// STEP only — the document sync endpoint does not resolve a requester.
			...(type === "step"
				? [{
					field: "requested_by",
					headerName: t("UI_TEXT.REQUESTED_BY", { defaultValue: "Requested By" }),
					flex: 1.2,
					minWidth: 190,
					type: "string",
					// A null user_id is not missing data — it means the pipeline raised
					// the request itself, with no human behind it.
					valueGetter: (value, row) => row?.requested_by || "Automated pipeline",
					renderCell: (params) => (
						<Box sx={{ lineHeight: 1.3, py: 0.5 }}>
							<Typography variant="body2">{params?.value}</Typography>
							{params?.row?.requested_by_email && (
								<Typography variant="caption" color="text.secondary">
									{params.row.requested_by_email}
								</Typography>
							)}
						</Box>
					),
				}]
				: []),
			{
				field: "updated_at",
				headerName: t("UI_TEXT.UPDATED"),
				flex: 1.2,
				minWidth: 190,
				type: "string",
				valueFormatter: (value) => formatDateTime(value),
			},
			{
				field: "last_error",
				headerName: t("UI_TEXT.ERROR"),
				flex: 1.6,
				minWidth: 260,
				type: "string",
				renderCell: (params) => (
					<Typography
						variant="body2"
						sx={{ whiteSpace: "normal", wordBreak: "break-word", py: 1 }}
					>
						{params.value || "—"}
					</Typography>
				),
			},
		];

		if (type !== "step") {
			return baseColumns;
		}

		const activeSet = [
			"pending",
			"processing",
			"ready_to_submit",
			"awaiting_sharepoint",
			"submitted_to_model_convertor",
			"conversion_completed",
			"finalizing_model",
		];
		const queuedSet = ["pending", "ready_to_submit", "awaiting_sharepoint"];
		const terminalSet = ["complete", "completed", "cancelled", "up_to_date", "superseded"];

		const ActionBtn = ({ row, action, title, color, icon, disabled }) => {
			const isBusy = busy?.id === row?.id && busy?.action === action;
			return (
				<Tooltip title={title} placement="top" arrow>
					<Box component="span">
						<IconButton
							size="small"
							color={color}
							disabled={disabled || isBusy}
							onClick={() => {
								if (action === "start") onStartStep?.(row);
								else if (action === "hold") onHoldStep?.(row);
								else if (action === "cancel") onCancelStep?.(row);
								else if (action === "delete") onDeleteStep?.(row);
								else if (action === "timeline") onViewTimeline?.(row);
							}}
						>
							{isBusy ? <CircularProgress size={16} /> : icon}
						</IconButton>
					</Box>
				</Tooltip>
			);
		};

		return [
			...baseColumns,
				{
					field: "actions",
					headerName: t("TABLE_HEADERS.ACTIONS"),
					flex: 1.4,
					minWidth: 240,
				sortable: false,
				filterable: false,
				hideable: false,
					renderCell: (params) => {
						const row = params.row;
						const status = normalizeValue(row?.status);
						const isActive = activeSet.includes(status);
						const isQueued = queuedSet.includes(status);
						const isTerminal = terminalSet.includes(status);

						return (
							<Box sx={{ width: "100%", display: "flex", justifyContent: "center", alignItems: "center" }}>
								<ActionBtn row={row} action="timeline" title={t("UI_TEXT.VIEW_TIMELINE", { defaultValue: "Live timeline" })} color="primary" icon={<TimelineIcon fontSize="small" />} />
								<ActionBtn row={row} action="start" title={t("UI_TEXT.START_RERUN", { defaultValue: "Start / re-run" })} color="success" icon={<PlayArrowIcon fontSize="small" />} disabled={isActive} />
								<ActionBtn row={row} action="hold" title={t("UI_TEXT.HOLD", { defaultValue: "Hold" })} color="warning" icon={<PauseIcon fontSize="small" />} disabled={!isQueued} />
								<ActionBtn row={row} action="cancel" title={t("UI_TEXT.CANCEL_KILL", { defaultValue: "Cancel / kill" })} color="error" icon={<CancelIcon fontSize="small" />} disabled={isTerminal} />
								<ActionBtn row={row} action="delete" title={t("UI_TEXT.REMOVE", { defaultValue: "Remove" })} color="error" icon={<DeleteIcon fontSize="small" />} />
							</Box>
						);
					},
				},
			];
	}, [busy, onCancelStep, onDeleteStep, onHoldStep, onStartStep, onViewTimeline, t, type]);

	return (
		<Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
			<Stack direction={{ xs: "column", md: "row" }} spacing={2} justifyContent="space-between" alignItems={{ xs: "stretch", md: "center" }} sx={{ mb: 2 }}>
					<Box>
						<Typography variant="h6">{title}</Typography>
						<Typography variant="body2" color="text.secondary">
							{type === "step" ? t("UI_TEXT.LATEST_STEP_SYNC_REQUESTS") : t("UI_TEXT.LATEST_DOCUMENT_SYNC_REQUESTS")}
						</Typography>
					</Box>
					<Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ xs: "stretch", sm: "center" }}>
						<TextField
							variant="outlined"
							size="small"
							value={searchValue || ""}
							onChange={(e) => onSearchChange?.(e.target.value)}
							placeholder={t("PLACEHOLDER.SEARCH", { defaultValue: "Search" })}
							disabled={!tableExists}
							sx={{ minWidth: { xs: "100%", sm: 240 } }}
							InputProps={{
								endAdornment: (
									<InputAdornment position="end">
										<SearchIcon fontSize="small" />
									</InputAdornment>
								),
							}}
						/>
						<Button
							variant="contained"
							color="error"
							onClick={onClearLogs}
							disabled={loading || clearLogsLoading || !tableExists}
						>
							{clearLogsLoading ? <CircularProgress size={18} color="inherit" /> : "Clear All Logs"}
						</Button>
					</Stack>
				</Stack>
			{/* Machine / site / completed-date filters. Separate from the grid's own
			    filter panel because these are the three questions actually asked of this
			    screen ("where is machine X", "what did site Y get", "what finished
			    today"), and the panel hides them two clicks deep. */}
			{type === "step" && (
				<Stack
					direction="row"
					spacing={1.5}
					alignItems="center"
					sx={{ mb: 2, flexWrap: "wrap", rowGap: 1.5 }}
				>
					<MultiFilterSelect
						label={t("UI_TEXT.SITE", { defaultValue: "Site" })}
						options={siteOptions}
						value={quickFilters.site || []}
						onChange={(next) => onQuickFilterChange?.("site", next)}
						loading={optionsLoading}
						disabled={!tableExists}
						width={260}
					/>
					<MultiFilterSelect
						label={t("UI_TEXT.MACHINE")}
						options={machineOptions}
						value={quickFilters.machine_number || []}
						onChange={(next) => onQuickFilterChange?.("machine_number", next)}
						onOpen={onLoadMachineOptions}
						loading={machineOptionsLoading}
						disabled={!tableExists}
						width={260}
					/>
					<MultiFilterSelect
						label={t("UI_TEXT.STATUS")}
						options={STATUS_FILTER_OPTIONS}
						value={quickFilters.status || []}
						onChange={(next) => onQuickFilterChange?.("status", next)}
						disabled={!tableExists}
						width={240}
					/>
					<TextField
						size="small"
						type="date"
						label={t("UI_TEXT.COMPLETED_FROM", { defaultValue: "Completed from" })}
						InputLabelProps={{ shrink: true }}
						value={quickFilters.completed_from || ""}
						onChange={(e) => onQuickFilterChange?.("completed_from", e.target.value)}
						disabled={!tableExists}
						sx={{ minWidth: 175 }}
					/>
					<TextField
						size="small"
						type="date"
						label={t("UI_TEXT.COMPLETED_TO", { defaultValue: "Completed to" })}
						InputLabelProps={{ shrink: true }}
						value={quickFilters.completed_to || ""}
						onChange={(e) => onQuickFilterChange?.("completed_to", e.target.value)}
						disabled={!tableExists}
						sx={{ minWidth: 175 }}
					/>
					<Button size="small" onClick={() => onQuickFilterReset?.()} disabled={!tableExists}>
						{t("UI_TEXT.CLEAR_FILTERS", { defaultValue: "Clear filters" })}
					</Button>
				</Stack>
			)}
			{!tableExists ? (
				<Alert severity="info">{t("UI_TEXT.SOURCE_TABLE_NOT_AVAILABLE")}</Alert>
			) : (
				<Datagrid
					data={rows}
					columns={columns}
					rowIdField="id"
					pageSize={paginationModel?.pageSize || 10}
					pageSizeOptions={[5, 10, 25, 50]}
					showSearchBar={false}
					checkboxSelection={type === "step"}
					rowSelectionModel={type === "step" ? selectedIds : undefined}
					onSelect={type === "step" ? onSelectionChange : undefined}
					customButtons={bulkButtons}
					keepNonExistentSelected
					autoHeight
					sortBy={[{ field: "updated_at", sort: "desc" }]}
					paginationMode="server"
					rowCount={rowCount || 0}
					paginationModel={paginationModel}
					onPaginationModelChange={onPaginationModelChange}
					filterMode="server"
					filterModel={filterModel}
					onFilterModelChange={onFilterModelChange}
					loading={loading}
					localeText={{
						noRowsLabel: t("UI_TEXT.NO_RECORDS_FOUND"),
					}}
				/>
			)}
		</Paper>
	);
};

// Live per-request event timeline. Loads history via REST, then streams new events
// over the existing Socket.io connection (server bridge relays Postgres NOTIFY).
const StepTimelineDrawer = ({ row, onClose }) => {
	const open = Boolean(row);
	const requestId = row?.id;
	const [events, setEvents] = useState([]);
	const [loading, setLoading] = useState(false);
	// Whether the event socket is connected — an input to the header chip, not the
	// header chip itself: a finished request is "Completed" whether or not a socket
	// happens to be open.
	const [live, setLive] = useState(false);
	const runState = useMemo(() => runStateChip(row, live), [row, live]);

	useEffect(() => {
		if (!requestId) return undefined;
		let cancelled = false;
		const byId = new Map();
		const upsert = (list) => {
			for (const e of list) {
				if (e && e.id != null) byId.set(Number(e.id), e);
			}
			const merged = Array.from(byId.values()).sort((a, b) => {
				const ta = new Date(a.created_at).getTime() || 0;
				const tb = new Date(b.created_at).getTime() || 0;
				return ta - tb || Number(a.id) - Number(b.id);
			});
			if (!cancelled) setEvents(merged);
		};

		setLoading(true);
		setEvents([]);
		getStepSyncRequestEvents(requestId)
			.then((resp) => {
				if (resp?.status) upsert(resp?.data?.rows || []);
			})
			.finally(() => {
				if (!cancelled) setLoading(false);
			});

		let socket;
		const onEvent = (evt) => {
			if (!evt || Number(evt.request_id) !== Number(requestId)) return;
			upsert([evt]);
		};
		const subscribe = () => {
			try {
				socket.emit("step-sync:subscribe", { requestId });
			} catch (e) {
				/* ignore */
			}
		};
		const onConnect = () => {
			setLive(true);
			subscribe();
		};
		const onDisconnect = () => setLive(false);
		try {
			socket = getSocket();
			socket.on("step-sync:event", onEvent);
			socket.on("connect", onConnect);
			socket.on("disconnect", onDisconnect);
			setLive(!!socket.connected);
			subscribe();
		} catch (e) {
			/* socket optional — REST history still shows */
		}

		return () => {
			cancelled = true;
			if (socket) {
				try {
					socket.emit("step-sync:unsubscribe", { requestId });
				} catch (e) {
					/* ignore */
				}
				socket.off("step-sync:event", onEvent);
				socket.off("connect", onConnect);
				socket.off("disconnect", onDisconnect);
			}
		};
	}, [requestId]);

	return (
		<Drawer
			anchor="right"
			open={open}
			onClose={onClose}
			PaperProps={{ sx: { width: { xs: "100%", sm: 460 }, p: 2 } }}
		>
			<Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
				<Box>
					<Typography variant="h6">Request #{requestId}</Typography>
					{/* Site first, then the machine: a request is only meaningful once you
					    know whose machine it is, and the grid row alone doesn't travel with
					    a screenshot of this drawer. */}
					<Typography variant="body2" color="text.secondary">
						{row?.customer_site_number || "—"}
						{row?.customer_site_name ? ` · ${row.customer_site_name}` : ""}
					</Typography>
					<Stack direction="row" spacing={0.75} alignItems="center" sx={{ mt: 0.5, flexWrap: "wrap" }}>
						<Typography variant="body2">{row?.machine_number || "—"}</Typography>
						{row?.item_number && row.item_number !== row.machine_number && (
							<Typography variant="body2" color="text.secondary">
								/ {row.item_number}
							</Typography>
						)}
						{partRole(row) && (
							<Chip
								size="small"
								variant="outlined"
								label={partRole(row)}
								color={partRole(row) === "Parent" ? "secondary" : "default"}
							/>
						)}
					</Stack>
				</Box>
				<Stack direction="row" spacing={1} alignItems="center">
					<Chip size="small" color={runState.color} label={runState.label} />
					<IconButton onClick={onClose} size="small">
						<CloseIcon />
					</IconButton>
				</Stack>
			</Stack>
			<Divider sx={{ mb: 2 }} />
			{loading && events.length === 0 ? (
				<Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
					<CircularProgress size={22} />
				</Box>
			) : events.length === 0 ? (
				<Alert severity="info">No events yet for this request.</Alert>
			) : (
				<Stack spacing={0}>
					{events.map((e, idx) => (
						<Box key={e.id ?? idx} sx={{ display: "flex", gap: 1.5 }}>
							<Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
								<FiberManualRecordIcon sx={{ fontSize: 14, color: `${statusColor(e.status)}.main` }} />
								{idx < events.length - 1 && (
									<Box sx={{ flexGrow: 1, width: "2px", bgcolor: "divider", my: 0.5 }} />
								)}
							</Box>
							<Box sx={{ pb: 2, flexGrow: 1 }}>
								<Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
									<Typography variant="subtitle2">{STAGE_LABEL[e.stage] || e.stage}</Typography>
									<Chip size="small" color={statusColor(e.status)} label={e.status} />
									{e.source && <Chip size="small" variant="outlined" label={e.source} />}
								</Stack>
								<Typography variant="caption" color="text.secondary">
									{formatDateTime(e.created_at)}
								</Typography>
								{e.error_text && (
									<Typography variant="body2" color="error" sx={{ mt: 0.5, wordBreak: "break-word" }}>
										{e.error_text}
									</Typography>
								)}
							</Box>
						</Box>
					))}
				</Stack>
			)}
		</Drawer>
	);
};

const PipelineMonitor = () => {
	const { t } = useTranslation();
	const [summary, setSummary] = useState(null);
	const [stepRows, setStepRows] = useState([]);
	const [docRows, setDocRows] = useState([]);
	const [stepTableExists, setStepTableExists] = useState(true);
	const [docTableExists, setDocTableExists] = useState(true);
	const [stepTotalRows, setStepTotalRows] = useState(0);
	const [docTotalRows, setDocTotalRows] = useState(0);
	const [stepPaginationModel, setStepPaginationModel] = useState({ page: 0, pageSize: 10 });
	const [docPaginationModel, setDocPaginationModel] = useState({ page: 0, pageSize: 10 });
	const [stepFilterModel, setStepFilterModel] = useState({ items: [] });
	const [docFilterModel, setDocFilterModel] = useState({ items: [] });
	// Toolbar filters (machine / site / completed-date range). Held as two copies for
	// the same reason as the search box: `Input` is what the user is typing right now,
	// the debounced copy is what the server query is keyed on.
	const EMPTY_QUICK_FILTERS = { machine_number: [], site: [], status: [], completed_from: "", completed_to: "" };
	const [stepQuickInput, setStepQuickInput] = useState(EMPTY_QUICK_FILTERS);
	const [stepQuickFilters, setStepQuickFilters] = useState(EMPTY_QUICK_FILTERS);
	// Raw text bound to the search box vs. the debounced value sent to the server.
	const [stepSearchInput, setStepSearchInput] = useState("");
	const [docSearchInput, setDocSearchInput] = useState("");
	const [stepSearch, setStepSearch] = useState("");
	const [docSearch, setDocSearch] = useState("");
	const [loading, setLoading] = useState(true);
	const [stepLoading, setStepLoading] = useState(true);
	const [docLoading, setDocLoading] = useState(true);
	const [busy, setBusy] = useState({ id: null, action: null });
	const [stepSelectedIds, setStepSelectedIds] = useState([]);
	const [selectAllBusy, setSelectAllBusy] = useState(false);
	const [siteOptions, setSiteOptions] = useState([]);
	const [machineOptions, setMachineOptions] = useState([]);
	const [optionsLoading, setOptionsLoading] = useState(false);
	const [machineOptionsLoading, setMachineOptionsLoading] = useState(false);
	const machineOptionsRequestedRef = useRef(false);
	const [bulkBusy, setBulkBusy] = useState(null);
	const [clearLogsLoadingType, setClearLogsLoadingType] = useState("");
	const [error, setError] = useState("");
	const [activeTab, setActiveTab] = useState(0);
	const [timelineRow, setTimelineRow] = useState(null);

	const loadSummary = useCallback(async () => {
		const response = await getPipelineMonitorSummary();
		if (response?.status) {
			setSummary(response.data || null);
			setStepTableExists(response?.data?.step_sync?.table_exists !== false);
			setDocTableExists(response?.data?.document_sync?.table_exists !== false);
			setError("");
			return;
		}
		setError(response?.message || "Failed to load pipeline monitor summary.");
	}, []);

	// Everything except limit/offset that identifies "the current view". Shared by the
	// grid load and Select-all so the two can never disagree about what is being
	// looked at — selecting all of a filtered set and then exporting a different set
	// would be the worst kind of quiet bug.
	const stepQueryParams = useMemo(() => {
		// Multi-selects go on the wire comma-joined; the server splits them back out
		// and ORs the values within a filter while ANDing across filters.
		const toolbar = {};
		for (const [key, value] of Object.entries(stepQuickFilters)) {
			const serialized = Array.isArray(value) ? value.join(",") : String(value || "").trim();
			// Empty ones are dropped rather than sent blank — a blank machine_number
			// would otherwise overwrite (and silently cancel) the grid panel's filter.
			if (serialized) toolbar[key] = serialized;
		}
		return {
			...(stepSearch ? { search: stepSearch } : {}),
			...buildRequestFilters(stepFilterModel),
			// Toolbar filters last, so an explicit pick wins over the same field set in
			// the grid's own filter panel.
			...toolbar,
		};
	}, [stepSearch, stepFilterModel, stepQuickFilters]);

	const loadStepRows = useCallback(async (model = stepPaginationModel) => {
		setStepLoading(true);
		try {
			const limit = Number(model?.pageSize) || 10;
			const page = Number(model?.page) || 0;
			const response = await getStepSyncRequests({
				limit,
				offset: page * limit,
				...stepQueryParams,
			});
			if (response?.status) {
				setStepRows(sanitizeRequestRows(response?.data?.rows));
				setStepTotalRows(Number(response?.data?.total || 0));
				setStepTableExists(response?.data?.table_exists !== false);
			}
		} finally {
			setStepLoading(false);
		}
	}, [stepPaginationModel, stepQueryParams]);

	// Sites are loaded on mount: one query, and the picker is the first thing an admin
	// reaches for. A failure here must not take the grid down with it — the log is
	// still readable and every other filter still works.
	useEffect(() => {
		let cancelled = false;
		setOptionsLoading(true);
		getAllCustomerSites()
			.then((response) => {
				if (cancelled || !response?.status) return;
				setSiteOptions(toFilterOptions(response.data, "customer_site_number", "customer_site_name"));
			})
			.catch(() => {})
			.finally(() => {
				if (!cancelled) setOptionsLoading(false);
			});
		return () => {
			cancelled = true;
		};
	}, []);

	/**
	 * Load the machine picker's options, once, the first time it is opened.
	 *
	 * Deliberately NOT on mount: /machines/by-provision is unpaginated and runs a
	 * correlated per-row lookup against step_sync_requests, so firing it every time
	 * anyone opens Pipeline Logs would put a heavy query behind a screen most visits
	 * never filter by machine at all.
	 *
	 * @returns {Promise<void>} Resolves when the options are in state.
	 */
	const loadMachineOptions = useCallback(async () => {
		if (machineOptionsRequestedRef.current) return;
		machineOptionsRequestedRef.current = true;
		setMachineOptionsLoading(true);
		try {
			const response = await getMachinesByProvision();
			if (response?.status) {
				setMachineOptions(toFilterOptions(response.data, "machine_number", "machine_name"));
			}
		} catch (error) {
			// Let the user retry by reopening the dropdown.
			machineOptionsRequestedRef.current = false;
		} finally {
			setMachineOptionsLoading(false);
		}
	}, []);

	// id -> row for every row this session has loaded. Selection is by id, but Export
	// needs the whole row, and with Select-all the selected rows are mostly NOT on the
	// current page — filtering the visible page by id would export a handful of rows
	// and look like the export silently dropped the rest.
	const stepRowCacheRef = useRef(new Map());
	useEffect(() => {
		for (const row of stepRows) {
			if (row?.id !== null && row?.id !== undefined) stepRowCacheRef.current.set(String(row.id), row);
		}
	}, [stepRows]);

	/**
	 * Select every request matching the current filters, not just the visible page.
	 *
	 * The grid's header checkbox is page-scoped by design, so "export everything that
	 * completed today" otherwise means paging through and ticking each page. Walks the
	 * same query the grid is showing, in one unpaged request, and selects the lot.
	 *
	 * @returns {Promise<void>} Resolves once the selection is set.
	 */
	const handleSelectAllMatching = useCallback(async () => {
		setSelectAllBusy(true);
		try {
			const ids = [];
			// One unpaged request. This was a loop over the paged grid endpoint — 200
			// rows at a time, sequentially because offset paging over a table the
			// pipeline is actively writing to can skip or repeat rows — and it gave up
			// at 4000. That was ~90 round trips on a large filter, and the cap silently
			// decided what "everything" meant. The export endpoint returns the whole
			// filtered set in one go, carrying only the columns the sheet prints.
			const response = await exportStepSyncRequests(stepQueryParams);
			const rows = response?.status ? sanitizeRequestRows(response?.data?.rows) : [];
			for (const row of rows) {
				stepRowCacheRef.current.set(String(row.id), row);
				ids.push(row.id);
			}
			const total = Number(response?.data?.total || rows.length);
			setStepSelectedIds(ids);
			// The server's own guard only trips in the tens of thousands, but report it
			// rather than let a clipped file look complete.
			if (total > ids.length) {
				Swal.fire({
					icon: "info",
					title: t("UI_TEXT.SELECTION_LIMITED", { defaultValue: "Selection limited" }),
					text: `Selected ${ids.length} of ${total} matching requests. Narrow the filters to cover the rest.`,
				});
			}
		} finally {
			setSelectAllBusy(false);
		}
	}, [stepQueryParams, t]);

	/**
	 * Export the current selection, pulling whole rows from the session cache.
	 *
	 * @returns {void} Triggers the .xlsx download.
	 */
	const handleExportSelected = useCallback(() => {
		const rows = stepSelectedIds
			.map((id) => stepRowCacheRef.current.get(String(id)))
			.filter(Boolean);
		exportStepRowsToXLSX(rows);
	}, [stepSelectedIds]);

	const loadDocRows = useCallback(async (model = docPaginationModel) => {
		setDocLoading(true);
		try {
			const limit = Number(model?.pageSize) || 10;
			const page = Number(model?.page) || 0;
			const response = await getDocumentSyncRequests({
				limit,
				offset: page * limit,
				...(docSearch ? { search: docSearch } : {}),
				...buildRequestFilters(docFilterModel),
			});
			if (response?.status) {
				setDocRows(sanitizeRequestRows(response?.data?.rows));
				setDocTotalRows(Number(response?.data?.total || 0));
				setDocTableExists(response?.data?.table_exists !== false);
			}
		} finally {
			setDocLoading(false);
		}
	}, [docFilterModel, docPaginationModel, docSearch]);

	const refreshAll = useCallback(async () => {
		setLoading(true);
		try {
			await Promise.all([loadSummary(), loadStepRows(), loadDocRows()]);
		} finally {
			setLoading(false);
		}
	}, [loadDocRows, loadStepRows, loadSummary]);

	useEffect(() => {
		refreshAll();
	}, [refreshAll]);

	useEffect(() => {
		loadStepRows(stepPaginationModel);
	}, [loadStepRows, stepPaginationModel, stepFilterModel]);

	// Drop the selection whenever the query behind the grid changes. Without this, a
	// "Select all (30)" against Status=pending stays selected after switching to
	// Status=complete: the toolbar still reads "Export (30)" and the export writes the
	// 30 PENDING rows out of the row cache — the wrong data, with nothing on screen
	// saying so. Paging does not change stepQueryParams, so a cross-page selection
	// survives paging, which is the whole point of Select all.
	useEffect(() => {
		setStepSelectedIds([]);
	}, [stepQueryParams]);

	useEffect(() => {
		loadDocRows(docPaginationModel);
	}, [docFilterModel, docPaginationModel, loadDocRows]);

	// Debounce the search boxes and reset to the first page when the term changes,
	// so the server query stays in sync with what the user typed.
	useEffect(() => {
		const handle = setTimeout(() => {
			const next = stepSearchInput.trim();
			setStepSearch((prev) => (prev === next ? prev : next));
			setStepPaginationModel((model) => (model.page === 0 ? model : { ...model, page: 0 }));
		}, 400);
		return () => clearTimeout(handle);
	}, [stepSearchInput]);

	useEffect(() => {
		const handle = setTimeout(() => {
			const next = docSearchInput.trim();
			setDocSearch((prev) => (prev === next ? prev : next));
			setDocPaginationModel((model) => (model.page === 0 ? model : { ...model, page: 0 }));
		}, 400);
		return () => clearTimeout(handle);
	}, [docSearchInput]);

	// Same debounce for the toolbar filters. The date pickers don't need it, but going
	// through one path keeps a typed machine number and a picked date from firing two
	// separate queries when they're changed together.
	useEffect(() => {
		const handle = setTimeout(() => {
			// Compared serialised, not by reference: the pickers hand back a fresh array
			// on every render, which would otherwise refetch the grid endlessly.
			setStepQuickFilters((prev) =>
				JSON.stringify(prev) === JSON.stringify(stepQuickInput) ? prev : stepQuickInput
			);
			setStepPaginationModel((model) => (model.page === 0 ? model : { ...model, page: 0 }));
		}, 400);
		return () => clearTimeout(handle);
	}, [stepQuickInput]);

	// Keep the latest step-table refetch closure (current page + filters) in a ref so the
	// long-lived socket subscription below never needs to resubscribe when they change.
	const stepFeedRefetchRef = useRef(null);
	useEffect(() => {
		stepFeedRefetchRef.current = () => loadStepRows(stepPaginationModel);
	}, [loadStepRows, stepPaginationModel]);

	// Live whole-table feed (STEP only): the server broadcasts "step-sync:event" to the
	// "step-sync:feed" room. Join it once, then debounce a refetch of the current page so
	// the main table updates live instead of relying solely on REST polling. This is
	// independent of the per-request subscription used by StepTimelineDrawer.
	useEffect(() => {
		let socket;
		let timer = null;
		const scheduleRefetch = () => {
			if (timer) clearTimeout(timer);
			timer = setTimeout(() => {
				timer = null;
				stepFeedRefetchRef.current?.();
			}, 800);
		};
		const subscribe = () => {
			try {
				socket.emit("step-sync:subscribe", { feed: true });
			} catch (e) {
				/* ignore */
			}
		};
		try {
			socket = getSocket();
			socket.on("step-sync:event", scheduleRefetch);
			socket.on("connect", subscribe);
			subscribe();
		} catch (e) {
			/* socket optional — REST refresh still applies */
		}
		return () => {
			if (timer) clearTimeout(timer);
			if (socket) {
				try {
					socket.emit("step-sync:unsubscribe", { feed: true });
				} catch (e) {
					/* ignore */
				}
				socket.off("step-sync:event", scheduleRefetch);
				socket.off("connect", subscribe);
			}
		};
	}, []);

	const runStepAction = useCallback(
		async (row, action, apiCall, { confirm } = {}) => {
			if (!row?.id) return;
			if (confirm) {
				const c = await Swal.fire({
					title: confirm.title,
					text: confirm.text,
					icon: "warning",
					showCancelButton: true,
					confirmButtonColor: "#d33",
					cancelButtonColor: "#6c757d",
					confirmButtonText: confirm.button || t("GENERAL.YES", { defaultValue: "Yes" }),
				});
				if (!c.isConfirmed) return;
			}
			setBusy({ id: row.id, action });
			try {
				const response = await apiCall(row.id);
				if (!response?.status) {
					setError(response?.message || "Action failed.");
					return;
				}
				setError("");
				await Promise.all([loadSummary(), loadStepRows(stepPaginationModel)]);
			} finally {
				setBusy({ id: null, action: null });
			}
		},
		[loadStepRows, loadSummary, stepPaginationModel, t]
	);

	/**
	 * Re-run one STEP sync request, asking first whether to force a rebuild.
	 *
	 * @param {{id: number, machine_number?: string}} row The request row.
	 * @returns {Promise<void>}
	 *
	 * The mode is captured here and closed over rather than threaded through
	 * `runStepAction`, which passes only the id to its `apiCall`. Prompting inside
	 * `runStepAction` instead would put the question on hold/cancel/delete too.
	 */
	const handleStartStep = useCallback(
		async (row) => {
			const mode = await askRefreshMode({
				title: t("UI_TEXT.START_RERUN", { defaultValue: "Start / re-run" }),
				text:
					`Re-run request #${row?.id} (${row?.machine_number || "—"}). ` +
					"Simple refresh converts only if the CAD source changed or the model is missing. " +
					"Hard refresh rebuilds the model either way.",
			});
			if (!mode) return;
			await runStepAction(row, "start", (id) => resetStepSyncRequest(id, mode));
		},
		[runStepAction, t]
	);
	const handleHoldStep = useCallback((row) => runStepAction(row, "hold", holdStepSyncRequest), [runStepAction]);
	const handleCancelStep = useCallback(
		(row) =>
			runStepAction(row, "cancel", cancelStepSyncRequest, {
				confirm: {
					title: t("UI_TEXT.CANCEL_KILL", { defaultValue: "Cancel / kill" }),
					text: `Cancel STEP sync request #${row?.id} (${row?.machine_number || "—"})?`,
					button: t("GENERAL.YES", { defaultValue: "Yes" }),
				},
			}),
		[runStepAction, t]
	);
	const handleDeleteStep = useCallback(
		(row) =>
			runStepAction(row, "delete", deleteStepSyncRequest, {
				confirm: {
					title: t("UI_TEXT.REMOVE", { defaultValue: "Remove" }),
					text: `Permanently remove request #${row?.id} and its events?`,
					button: t("GENERAL.YES_DELETE_IT", { defaultValue: "Yes, delete it" }),
				},
			}),
		[runStepAction, t]
	);

	const runBulkAction = useCallback(
		async (action, apiCall, { confirm } = {}) => {
			const ids = stepSelectedIds || [];
			if (!ids.length) return;
			if (confirm) {
				const c = await Swal.fire({
					title: confirm.title,
					text: `${confirm.text} (${ids.length} selected)`,
					icon: "warning",
					showCancelButton: true,
					confirmButtonColor: "#d33",
					cancelButtonColor: "#6c757d",
					confirmButtonText: confirm.button || t("GENERAL.YES", { defaultValue: "Yes" }),
				});
				if (!c.isConfirmed) return;
			}
			setBulkBusy(action);
			try {
				const results = await Promise.allSettled(ids.map((id) => apiCall(id)));
				const failed = results.filter((r) => r.status === "rejected" || r.value?.status === false).length;
				setError(failed ? `${failed} of ${ids.length} request(s) failed.` : "");
				setStepSelectedIds([]);
				await Promise.all([loadSummary(), loadStepRows(stepPaginationModel)]);
			} finally {
				setBulkBusy(null);
			}
		},
		[stepSelectedIds, loadStepRows, loadSummary, stepPaginationModel, t]
	);

	/**
	 * Re-run every selected STEP sync request, asking once for the refresh mode.
	 *
	 * @returns {Promise<void>}
	 */
	const handleBulkStart = useCallback(async () => {
		const mode = await askRefreshMode({
			title: t("UI_TEXT.START_RERUN", { defaultValue: "Start / re-run" }),
			text:
				"Re-run the selected STEP sync requests. " +
				"Simple refresh converts only what changed or is missing. " +
				"Hard refresh rebuilds every selected model.",
		});
		if (!mode) return;
		await runBulkAction("start", (id) => resetStepSyncRequest(id, mode));
	}, [runBulkAction, t]);
	const handleBulkPause = useCallback(() => runBulkAction("pause", holdStepSyncRequest), [runBulkAction]);
	const handleBulkStop = useCallback(
		() =>
			runBulkAction("stop", cancelStepSyncRequest, {
				confirm: { title: t("UI_TEXT.CANCEL_KILL", { defaultValue: "Cancel / kill" }), text: "Cancel the selected STEP sync requests?", button: t("GENERAL.YES", { defaultValue: "Yes" }) },
			}),
		[runBulkAction, t]
	);
	const handleBulkClear = useCallback(
		() =>
			runBulkAction("clear", deleteStepSyncRequest, {
				confirm: { title: t("UI_TEXT.REMOVE", { defaultValue: "Remove" }), text: "Permanently remove the selected requests and their events?", button: t("GENERAL.YES_DELETE_IT", { defaultValue: "Yes, delete it" }) },
			}),
		[runBulkAction, t]
	);

	const handleClearLogs = useCallback(
		async (type) => {
			const label = type === "step" ? "STEP sync" : "document sync";
			const confirmation = await Swal.fire({
				title: t("ALERT_MESSAGES.ARE_YOU_SURE", {
					defaultValue: "Are you sure?",
				}),
				text:
					type === "step"
						? "Clear all STEP sync logs from the database?"
						: "Clear all document sync logs from the database?",
				icon: "warning",
				showCancelButton: true,
				confirmButtonColor: "#d33",
				cancelButtonColor: "#6c757d",
				confirmButtonText: t("GENERAL.CLEAR_LOGS", {
					defaultValue: "Clear logs",
				}),
			});
			if (!confirmation.isConfirmed) return;
			setClearLogsLoadingType(type);
			try {
				const response =
					type === "step"
						? await clearStepSyncRequests()
						: await clearDocumentSyncRequests();
				if (!response?.status) {
					setError(response?.message || `Failed to clear ${label} logs.`);
					await Swal.fire({
						icon: "error",
						title: t("ALERT_MESSAGES.ERROR", {
							defaultValue: "Error",
						}),
						text: response?.message || `Failed to clear ${label} logs.`,
					});
					return;
				}
				setError("");
				if (type === "step") {
					setStepPaginationModel((prev) => ({ ...prev, page: 0 }));
				} else {
					setDocPaginationModel((prev) => ({ ...prev, page: 0 }));
				}
				await Promise.all([loadSummary(), loadStepRows(), loadDocRows()]);
				await Swal.fire({
					icon: "success",
					title: t("ALERT_MESSAGES.SUCCESS", {
						defaultValue: "Success",
					}),
					text:
						type === "step"
							? "STEP sync logs cleared successfully."
							: "Document sync logs cleared successfully.",
				});
			} finally {
				setClearLogsLoadingType("");
			}
		},
		[loadDocRows, loadStepRows, loadSummary, t]
	);

	const stepSummary = useMemo(() => summary?.step_sync || {}, [summary]);
	const docSummary = useMemo(() => summary?.document_sync || {}, [summary]);

	return (
		<Box>
			<HeaderTitle
				title={t("ITEMS_TEXT.PIPELINE_MONITOR")}
				subtitle={t("UI_TEXT.PIPELINE_MONITOR_SUBTITLE")}
				actions={
					<Button
						variant="roundedGreyBgSquareContained"
						color="primary"
						onClick={refreshAll}
						disabled={loading}
					>
						{loading ? (
							<CircularProgress size={18} color="inherit" />
						) : (
							<RefreshIcon />
						)}
					</Button>
				}
			/>

			{error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

			<Grid container spacing={2} sx={{ mb: 2 }}>
				<Grid item xs={12} md={6}>
					<StatCard title={t("UI_TEXT.STEP_SYNC")} totals={stepSummary.totals || {}} counts={stepSummary.counts || {}} t={t} />
				</Grid>
				<Grid item xs={12} md={6}>
					<StatCard title={t("UI_TEXT.DOCUMENT_SYNC")} totals={docSummary.totals || {}} counts={docSummary.counts || {}} t={t} />
				</Grid>
			</Grid>

			<Paper variant="outlined" sx={{ p: 0, overflow: "hidden", borderRadius: 2 }}>
				<Box sx={{ borderBottom: 1, borderColor: "divider", px: 2, pt: 1 }}>
					<Tabs value={activeTab} onChange={(_, value) => setActiveTab(value)}>
						<Tab label={t("UI_TEXT.STEP_SYNC_REQUESTS")} />
						<Tab label={t("UI_TEXT.DOCUMENT_SYNC_REQUESTS")} />
					</Tabs>
				</Box>
				<Box sx={{ p: 2 }}>
					{activeTab === 0 && (
						<PipelineTable
							title={t("UI_TEXT.STEP_SYNC_REQUESTS")}
							rows={stepRows}
							loading={stepLoading}
							tableExists={stepTableExists}
								type="step"
								t={t}
								onViewTimeline={setTimelineRow}
								onStartStep={handleStartStep}
								onHoldStep={handleHoldStep}
								onCancelStep={handleCancelStep}
								onDeleteStep={handleDeleteStep}
								busy={busy}
								selectedIds={stepSelectedIds}
								onSelectionChange={setStepSelectedIds}
								onBulkStart={handleBulkStart}
								onBulkPause={handleBulkPause}
								onBulkStop={handleBulkStop}
								onBulkClear={handleBulkClear}
								bulkBusy={bulkBusy}
								onClearLogs={() => handleClearLogs("step")}
								clearLogsLoading={clearLogsLoadingType === "step"}
							rowCount={stepTotalRows}
							paginationModel={stepPaginationModel}
							onPaginationModelChange={setStepPaginationModel}
							filterModel={stepFilterModel}
							onFilterModelChange={(model) => {
								setStepFilterModel(model);
								setStepPaginationModel((prev) => ({ ...prev, page: 0 }));
							}}
							searchValue={stepSearchInput}
							onSearchChange={setStepSearchInput}
							quickFilters={stepQuickInput}
							onQuickFilterChange={(field, value) =>
								setStepQuickInput((prev) => ({ ...prev, [field]: value }))
							}
							onQuickFilterReset={() => setStepQuickInput(EMPTY_QUICK_FILTERS)}
							onSelectAllMatching={handleSelectAllMatching}
							onClearSelection={() => setStepSelectedIds([])}
							onExportSelected={handleExportSelected}
							selectAllBusy={selectAllBusy}
							siteOptions={siteOptions}
							machineOptions={machineOptions}
							optionsLoading={optionsLoading}
							machineOptionsLoading={machineOptionsLoading}
							onLoadMachineOptions={loadMachineOptions}
						/>
					)}
					{activeTab === 1 && (
						<PipelineTable
							title={t("UI_TEXT.DOCUMENT_SYNC_REQUESTS")}
							rows={docRows}
							loading={docLoading}
							tableExists={docTableExists}
								type="doc"
								t={t}
								onClearLogs={() => handleClearLogs("doc")}
								clearLogsLoading={clearLogsLoadingType === "doc"}
							rowCount={docTotalRows}
							paginationModel={docPaginationModel}
							onPaginationModelChange={setDocPaginationModel}
							filterModel={docFilterModel}
							onFilterModelChange={(model) => {
								setDocFilterModel(model);
								setDocPaginationModel((prev) => ({ ...prev, page: 0 }));
							}}
							searchValue={docSearchInput}
							onSearchChange={setDocSearchInput}
						/>
					)}
				</Box>
			</Paper>

			<StepTimelineDrawer row={timelineRow} onClose={() => setTimelineRow(null)} />
		</Box>
	);
};

export default PipelineMonitor;
